import React, { Component, ErrorInfo, ReactNode } from 'react'
import { motion } from 'framer-motion'
import { HomeIcon, ArrowPathIcon } from '@heroicons/react/24/outline'

interface Props {
    children?: ReactNode
}

interface State {
    hasError: boolean
    error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null
    }

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error }
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Critical UI Error Caught:', error, errorInfo)
    }

    private handleReload = () => {
        window.location.reload()
    }

    private handleGoHome = () => {
        window.location.href = '/'
    }

    public render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-black flex items-center justify-center p-6 text-white font-sans overflow-hidden relative">
                    {/* Animated Background Orbs */}
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                        <motion.div
                            className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-orange-500/10 rounded-full blur-3xl"
                            animate={{
                                scale: [1, 1.1, 1],
                                opacity: [0.3, 0.4, 0.3],
                                x: [0, 20, 0],
                                y: [0, -20, 0]
                            }}
                            transition={{ duration: 10, repeat: Infinity }}
                        />
                        <motion.div
                            className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-orange-600/5 rounded-full blur-3xl"
                            animate={{
                                scale: [1.1, 1, 1.1],
                                opacity: [0.4, 0.3, 0.4],
                                x: [0, -30, 0],
                                y: [0, 30, 0]
                            }}
                            transition={{ duration: 12, repeat: Infinity, delay: 1 }}
                        />
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="w-full max-w-xl relative z-10"
                    >
                        <div className="relative overflow-hidden p-10 rounded-[2rem] border border-white/5 bg-white/[0.02] backdrop-blur-3xl shadow-2xl text-center">
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: 'spring', damping: 12, stiffness: 200 }}
                                className="w-24 h-24 mx-auto mb-8 bg-gradient-to-br from-orange-400 to-orange-600 rounded-3xl flex items-center justify-center shadow-[0_0_40px_rgba(251,146,60,0.3)]"
                            >
                                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </motion.div>

                            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                                Something drifted off course
                            </h1>

                            <p className="text-gray-400 text-lg mb-10 leading-relaxed max-w-md mx-auto">
                                Our advanced interface encountered an unexpected instability. We've logged the coordinates and our team is investigating.
                            </p>

                            {/* Error Details (Subtle) */}
                            <div className="mb-10 text-xs text-orange-500/40 font-mono overflow-hidden truncate px-4">
                                {this.state.error?.message || 'Unknown stability failure'}
                            </div>

                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <motion.button
                                    whileHover={{ scale: 1.02, backgroundColor: 'rgba(255, 107, 53, 1)' }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={this.handleReload}
                                    className="flex items-center justify-center gap-2 px-8 py-4 bg-orange-500 text-white rounded-2xl font-bold transition-all shadow-lg shadow-orange-500/20"
                                >
                                    <ArrowPathIcon className="w-5 h-5" />
                                    Attempt Recovery
                                </motion.button>

                                <motion.button
                                    whileHover={{ scale: 1.02, backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={this.handleGoHome}
                                    className="flex items-center justify-center gap-2 px-8 py-4 bg-white/5 border border-white/10 text-white rounded-2xl font-bold transition-all"
                                >
                                    <HomeIcon className="w-5 h-5" />
                                    Return Home
                                </motion.button>
                            </div>
                        </div>

                        <p className="mt-8 text-center text-gray-600 text-sm">
                            Ref: {Math.random().toString(36).substring(7).toUpperCase()}
                        </p>
                    </motion.div>
                </div>
            )
        }

        return this.props.children
    }
}

export default ErrorBoundary
